<template>
  <PublicShell :color="data && data.brand ? data.brand.color : null">
    <div v-if="loading" role="status"><span class="pb-sr-only">Loading…</span><span class="pb-skeleton prt-sk"></span></div>
    <div v-else-if="failed" class="pb-alert is-error pub-failed" role="alert"><i class="mdi mdi-wifi-off pb-ico" aria-hidden="true"></i><span>We couldn't open this page. Check your connection and try again.</span><button type="button" class="pb-button sm outline" @click="reload">Try again</button></div>
    <div v-else-if="gone" class="prt-message">
      <span class="pb-empty-icon" aria-hidden="true"><i class="mdi mdi-link-variant-off"></i></span>
      <h1 class="prt-title display-font">This report link isn't active</h1>
      <p class="pb-body">{{ gone }}</p>
    </div>
    <template v-else-if="data">
      <header class="prt-head">
        <div class="prt-agency"><img v-if="logo(sender.logo)" :src="logo(sender.logo)" alt="" class="prt-logo"><strong>{{ sender.name }}</strong></div>
        <h1 class="prt-title display-font">Testimonials for {{ data.brand.name }}</h1>
        <p class="pb-body">{{ formatDate(data.range.from) }} to {{ formatDate(data.range.to) }}</p>
      </header>
      <div class="prt-tiles">
        <div v-for="tile in tiles" :key="tile.key" class="prt-tile"><span class="prt-t-label">{{ tile.label }}</span><span class="prt-t-value">{{ number(data.totals[tile.key]) }}</span></div>
      </div>
      <p class="pb-muted prt-note">{{ data.note }}</p>

      <section class="prt-section" aria-labelledby="prt-months">
        <h2 id="prt-months" class="prt-h display-font">By month</h2>
        <div class="prt-table-wrap">
          <table class="prt-table">
            <thead><tr><th scope="col">Month</th><th v-for="tile in monthly" :key="tile.key" scope="col">{{ tile.label }}</th></tr></thead>
            <tbody><tr v-for="row in data.months" :key="row.month"><th scope="row">{{ monthName(row.month) }}</th>
              <td v-for="tile in monthly" :key="tile.key">{{ number(row[tile.key]) }}</td></tr></tbody>
          </table>
        </div>
      </section>

      <section v-if="data.recent.length" class="prt-section" aria-labelledby="prt-recent">
        <h2 id="prt-recent" class="prt-h display-font">Recently published</h2>
        <div class="prt-grid"><TestimonialCard v-for="item in data.recent" :key="item.ref" :item="item" show-date /></div>
      </section>
    </template>
  </PublicShell>
</template>

<script>
import PublicShell from '../components/PublicShell.vue';
import TestimonialCard from '../components/TestimonialCard.vue';
import { get, message } from '../api';
import { formatDate, number } from '../lib/format';
import { imageUrl } from '../publicUrls';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default {
  name: 'PublicReportView',
  components: { PublicShell, TestimonialCard },
  data: () => ({ data: null, loading: true, gone: '', failed: false,
    tiles: [{ key: 'submissions', label: 'Responses' }, { key: 'publishable', label: 'With permission to publish' }, { key: 'published', label: 'Published' },
      { key: 'form_visits', label: 'Form visits' }, { key: 'widget_views', label: 'Widget views' }, { key: 'wall_views', label: 'Wall views' }],
    monthly: [{ key: 'submissions', label: 'Responses' }, { key: 'published', label: 'Published' }, { key: 'form_visits', label: 'Form visits' },
      { key: 'widget_views', label: 'Widget views' }, { key: 'wall_views', label: 'Wall views' }] }),
  computed: {
    // The agency's own name and logo once it has set them up as an agency; until then the client's own brand,
    // never a workspace named after another client.
    sender() { const data = this.data || {}; return data.agency || data.brand || {}; }
  },
  async mounted() {
    try {
      this.data = await get('/public/reports/' + encodeURIComponent(this.$route.params.token));
      document.title = this.data.brand.name + ' · Testimonial report';
    } catch (error) {
      const status = error.response ? error.response.status : 0;
      if (status === 404 || status === 429) this.gone = message(error, 'This report link is no longer active. Ask your agency for a new one.');
      else this.failed = true;
    } finally { this.loading = false; }
  },
  methods: {
    number,
    reload() { window.location.reload(); },
    formatDate,
    logo(key) { return key ? imageUrl(key) : ''; },
    monthName(value) { const [year, month] = value.split('-').map(Number); return MONTHS[month - 1] + ' ' + year; }
  }
};
</script>

<style scoped>
.prt-head{margin-bottom:24px}
.prt-agency{display:flex;align-items:center;gap:12px;margin-bottom:20px;font-size:16px}
.prt-logo{height:44px;max-width:160px;object-fit:contain}
.prt-title{font:500 34px/1.2 var(--font-display)!important;margin:0 0 8px;letter-spacing:-.5px}
.prt-tiles{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(180px,100%),1fr));gap:12px}
.prt-tile{display:flex;flex-direction:column;gap:4px;padding:16px 18px;border:1px solid #E6DED2;border-radius:14px;background:#fff}
.prt-t-label{font:600 13px/1.35 var(--font-body);color:#4F4A45}
.prt-t-value{font:600 30px/1.15 var(--font-body);color:#1F2328}
.prt-note{margin:10px 0 0}
.prt-section{margin-top:34px}
.prt-h{font:500 24px/1.25 var(--font-display)!important;margin:0 0 14px}
.prt-table-wrap{overflow-x:auto;border:1px solid #E6DED2;border-radius:14px;background:#fff}
.prt-table{width:100%;border-collapse:collapse;font-size:14.5px}
.prt-table th,.prt-table td{padding:12px 16px;text-align:left;border-bottom:1px solid #EFE9E0;white-space:nowrap}
.prt-table thead th{background:#FBF8F3;font:600 12.5px var(--font-body);color:#4F4A45}
.prt-table td{font-variant-numeric:tabular-nums}
.prt-table tbody tr:last-child th,.prt-table tbody tr:last-child td{border-bottom:0}
.prt-grid{columns:300px;column-gap:18px}
.prt-grid>*{margin-bottom:18px;display:flex;width:100%;break-inside:avoid}
.prt-message{text-align:center;padding:48px 24px;background:#fff;border:1px solid #E6DED2;border-radius:20px}
.prt-sk{display:block;height:260px;border-radius:18px}
@media(max-width:600px){.prt-title{font-size:27px!important}}
</style>
